import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { EMAIL_TEMPLATES } from "../lib/email_templates";
import SEO from "../components/SEO";

export default function AdminBroadcast() {
  const [searchParams] = useSearchParams();
  const key = searchParams.get("key") || "";

  const [audience, setAudience] = useState(null);
  const [audienceError, setAudienceError] = useState(null);

  // Signed intro PDF inputs
  const [signerName, setSignerName] = useState("");
  const [signerTitle, setSignerTitle] = useState("");
  const [signerEmail, setSignerEmail] = useState("");

  const [subject, setSubject] = useState("");
  const [preheader, setPreheader] = useState("");
  const [html, setHtml] = useState("<h1>Hello from BrushBasics</h1>\n<p>Write your editorial drop here…</p>");
  const [testAddress, setTestAddress] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!key) return;
    api.get(`/email/audience?key=${encodeURIComponent(key)}`)
      .then((r) => setAudience(r.data))
      .catch((e) => setAudienceError(e?.response?.data?.detail || "Could not load audience"));
  }, [key]);

  const send = async (testOnly) => {
    setError(null);
    setResult(null);
    setSending(true);
    try {
      const payload = {
        subject: subject.trim(),
        preheader: preheader.trim(),
        html,
        test_only: !!testOnly,
        test_address: testOnly ? testAddress.trim() : undefined,
      };
      const { data } = await api.post(
        `/email/broadcast?key=${encodeURIComponent(key)}`,
        payload
      );
      setResult(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  if (!key) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center" data-testid="admin-no-key">
        <div className="overline mb-3">Admin</div>
        <h1 className="font-serif-display text-4xl text-stone-900">Missing key.</h1>
        <p className="mt-4 text-stone-600">
          This page is protected. Open it with <code>?key=YOUR_EXPORT_KEY</code> in the URL.
        </p>
      </div>
    );
  }

  if (audienceError) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center" data-testid="admin-forbidden">
        <div className="overline mb-3">Admin</div>
        <h1 className="font-serif-display text-4xl text-stone-900">Access denied.</h1>
        <p className="mt-4 text-stone-600">{audienceError}</p>
      </div>
    );
  }

  return (
    <div data-testid="admin-broadcast" className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
      <SEO title="Admin · Broadcast" description="BrushBasics admin broadcast tool." path="/admin/broadcast" noindex />
      <div className="overline mb-3">Admin · Broadcast</div>
      <h1 className="font-serif-display text-4xl sm:text-5xl text-stone-900 font-light tracking-tight">
        Write to your readers.
      </h1>
      <p className="mt-4 text-stone-600 max-w-2xl">
        {audience
          ? `Your list has ${audience.total} subscriber${audience.total === 1 ? "" : "s"}. Always send a test to yourself first.`
          : "Loading audience…"}
      </p>

      <div className="mt-6 bg-white border border-[#E5E0D8] p-5">
        <div className="overline mb-3">Intro PDF · for personal outreach</div>
        <p className="text-xs text-stone-500 mb-4 leading-relaxed">
          A 1-page editorial introduction. Add your name to sign it personally, or download the unsigned version. Attach to your own emails when reaching out one-to-one.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <input
            data-testid="pdf-signer-name"
            type="text"
            value={signerName}
            onChange={(e) => setSignerName(e.target.value)}
            placeholder="Your name"
            className="text-sm bg-[#F9F7F3] border border-[#E5E0D8] focus:border-[#8C6A5D] outline-none px-3 py-2.5"
          />
          <input
            data-testid="pdf-signer-title"
            type="text"
            value={signerTitle}
            onChange={(e) => setSignerTitle(e.target.value)}
            placeholder="Title (optional)"
            className="text-sm bg-[#F9F7F3] border border-[#E5E0D8] focus:border-[#8C6A5D] outline-none px-3 py-2.5"
          />
          <input
            data-testid="pdf-signer-email"
            type="email"
            value={signerEmail}
            onChange={(e) => setSignerEmail(e.target.value)}
            placeholder="Email (optional)"
            className="text-sm bg-[#F9F7F3] border border-[#E5E0D8] focus:border-[#8C6A5D] outline-none px-3 py-2.5"
          />
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <a
            data-testid="bc-download-intro-pdf"
            href={`${process.env.REACT_APP_BACKEND_URL}/api/email/intro-pdf?name=${encodeURIComponent(signerName)}&title=${encodeURIComponent(signerTitle)}&email=${encodeURIComponent(signerEmail)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs uppercase tracking-[0.22em] bg-[#8C6A5D] hover:bg-[#70544A] text-white px-6 py-3 transition-colors inline-flex items-center gap-2"
          >
            {signerName.trim() ? "Download Signed PDF" : "Download Unsigned PDF"}
            <span aria-hidden="true">↓</span>
          </a>
          {signerName.trim() && (
            <span className="text-xs text-stone-500" data-testid="pdf-signed-as">
              Will be signed: <em className="text-stone-900">{signerName.trim()}</em>
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mt-10">
        <div className="lg:col-span-7 space-y-5">
          <div data-testid="bc-templates">
            <label className="overline block mb-2">One-click templates</label>
            <div className="flex flex-wrap gap-2">
              {EMAIL_TEMPLATES.map((t) => (
                <button
                  key={t.id}
                  data-testid={`bc-template-${t.id}`}
                  type="button"
                  onClick={() => {
                    setSubject(t.subject);
                    setPreheader(t.preheader);
                    setHtml(t.html);
                    setResult(null);
                    setError(null);
                  }}
                  className="text-xs uppercase tracking-[0.18em] border border-[#E5E0D8] hover:border-[#8C6A5D] hover:text-[#8C6A5D] text-stone-700 bg-white px-4 py-2.5 transition-colors"
                >
                  {t.label}
                </button>
              ))}
            </div>
            <p className="text-xs text-stone-500 mt-2">
              Picks a pre-written email and drops it into the fields below. Edit freely, then send a test before broadcasting.
            </p>
          </div>

          <div>
            <label className="overline block mb-2" htmlFor="bc-subject">Subject</label>
            <input
              id="bc-subject"
              data-testid="bc-subject"
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="The Brush Edit · Volume 02"
              className="w-full text-sm bg-white border border-[#E5E0D8] focus:border-[#8C6A5D] outline-none px-3 py-3"
            />
          </div>
          <div>
            <label className="overline block mb-2" htmlFor="bc-preheader">Preheader (preview line)</label>
            <input
              id="bc-preheader"
              data-testid="bc-preheader"
              type="text"
              value={preheader}
              onChange={(e) => setPreheader(e.target.value)}
              placeholder="A new editorial drop, just landed."
              className="w-full text-sm bg-white border border-[#E5E0D8] focus:border-[#8C6A5D] outline-none px-3 py-3"
            />
          </div>
          <div>
            <label className="overline block mb-2" htmlFor="bc-html">Email body (HTML)</label>
            <textarea
              id="bc-html"
              data-testid="bc-html"
              value={html}
              onChange={(e) => setHtml(e.target.value)}
              rows={16}
              className="w-full text-sm font-mono bg-white border border-[#E5E0D8] focus:border-[#8C6A5D] outline-none px-3 py-3 leading-relaxed"
            />
            <p className="text-xs text-stone-500 mt-2">
              Your content is wrapped in the BrushBasics editorial shell (header + footer + unsubscribe). Use simple HTML — &lt;h1&gt;, &lt;p&gt;, &lt;a href&gt;, &lt;img&gt;.
            </p>
          </div>

          <div className="border-t border-[#E5E0D8] pt-5 space-y-4">
            <div>
              <label className="overline block mb-2" htmlFor="bc-test-addr">Send a test to yourself first</label>
              <input
                id="bc-test-addr"
                data-testid="bc-test-addr"
                type="email"
                value={testAddress}
                onChange={(e) => setTestAddress(e.target.value)}
                placeholder="you@example.com"
                className="w-full text-sm bg-white border border-[#E5E0D8] focus:border-[#8C6A5D] outline-none px-3 py-3"
              />
              <button
                data-testid="bc-send-test"
                onClick={() => send(true)}
                disabled={sending || !subject.trim() || !html.trim() || !testAddress.trim()}
                className="mt-3 text-xs uppercase tracking-[0.22em] border border-[#8C6A5D] text-[#8C6A5D] hover:bg-[#F2EFEB] disabled:opacity-40 px-6 py-3 transition-colors"
              >
                {sending ? "Sending…" : "Send Test"}
              </button>
            </div>

            <div className="flex items-center gap-4 flex-wrap">
              <button
                data-testid="bc-send-all"
                onClick={() => {
                  if (window.confirm(`Send to all ${audience?.total || 0} subscribers? This cannot be undone.`)) {
                    send(false);
                  }
                }}
                disabled={sending || !subject.trim() || !html.trim() || !audience?.total}
                className="text-xs uppercase tracking-[0.22em] bg-[#8C6A5D] hover:bg-[#70544A] disabled:bg-stone-300 text-white px-7 py-4 transition-colors"
              >
                {sending ? "Sending…" : `Send to All (${audience?.total ?? 0})`}
              </button>
              {error && <span data-testid="bc-error" className="text-sm text-red-700">{error}</span>}
              {result && (
                <div data-testid="bc-result" className="bg-[#F2EFEB] px-4 py-3 text-sm">
                  Sent <strong>{result.sent}</strong>
                  {result.failed_count ? `, failed ${result.failed_count}` : ""}{" "}
                  {result.test_only ? "(test)" : "(broadcast)"}
                </div>
              )}
            </div>
          </div>
        </div>

        <aside className="lg:col-span-5">
          <div className="bg-[#F2EFEB] p-6 sticky top-24">
            <div className="overline mb-3">Audience</div>
            <div className="font-serif-display text-5xl text-stone-900">{audience?.total ?? "—"}</div>
            <p className="text-sm text-stone-600 mt-1">total subscribers</p>

            <div className="mt-6">
              <div className="overline mb-2">Recent signups</div>
              <ul className="text-xs text-stone-700 space-y-1.5">
                {(audience?.recent || []).map((r, i) => (
                  <li key={i} className="flex justify-between gap-2">
                    <span className="truncate">{r.email}</span>
                    <span className="text-stone-500 shrink-0">{r.source || "—"}</span>
                  </li>
                ))}
              </ul>
            </div>

            <p className="text-xs text-stone-500 mt-8 leading-relaxed">
              Free Resend tier: 100 emails/day, 3,000/month. Without a verified sender domain, deliverability is limited — verify your domain in Resend before launching.
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}
